import '@babylonjs/core/Meshes/thinInstanceMesh';
import { WIND_UP, type Fighter } from '@archon3d/core';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

/** Pieces of arc round one fighter. Even, so the ring fills the same on both sides. */
export const RING_SEGMENTS = 24;
/** The share of each piece's arc left open, so the ring reads as counting. */
const GAP = 0.3;
/** Metres: how wide the ring's band is, and how far above the fighter's feet it lies. */
const BAND = 0.07;
const LIFT = 0.03;
/** A ready weapon's ring flashes white this long, then burns in its side's colour. */
const FLASH_SECONDS = 0.3;
const WHITE = new Color3(1, 1, 1);
/**
 * A blow on its way (D-42): while it is wound up, the whole ring burns this warning colour,
 * brighter as it comes to land, so the fighter it is aimed at can read it and step away.
 */
const WARNING = new Color3(1, 0.32, 0.08);

/** How far through its weapon's recovery a fighter is: 0 just struck, 1 ready. */
export function readiness(fighter: Pick<Fighter, 'cooldown' | 'attackInterval'>): number {
  if (!(fighter.attackInterval > 0)) return 1;
  return Math.min(1, Math.max(0, 1 - fighter.cooldown / fighter.attackInterval));
}

/**
 * Whether the piece of arc `i` of `RING_SEGMENTS` is lit at `fill`: the ring fills from behind
 * the fighter round both sides, and closes in front of it as its weapon comes ready.
 */
export function segmentLit(i: number, fill: number): boolean {
  // Pieces pair off across the fighter, counted from behind; the last pair is the one in front.
  const pair = Math.min(i, RING_SEGMENTS - 1 - i);
  return fill >= (pair + 1) / (RING_SEGMENTS / 2);
}

/**
 * How bright the warning burns while a blow winds up: from just over half at the press to full as
 * it lands. None when the fighter is not winding up.
 */
export function warningGlow(fighter: Pick<Fighter, 'windup'>): number | null {
  const winding = fighter.windup ?? 0;
  if (!(winding > 0)) return null;
  return 0.55 + 0.45 * Math.min(1, Math.max(0, 1 - winding / WIND_UP));
}

/** The ring's radius round a fighter: just clear of its body. */
export const ringRadius = (fighter: Pick<Fighter, 'halfWidth'>) =>
  Math.max(0.5, fighter.halfWidth + 0.25);

export interface RingFighter {
  fighter: Fighter;
  /** Where it is drawn this frame, which runs between two steps of the simulation. */
  pos: { x: number; y: number; z: number };
  yaw: number;
  color: Color3;
}

/**
 * A ring on the floor round each fighter that fills as its weapon recovers and flashes as it is
 * ready (x-aaa-gap#6): the wind-up read off the fighter itself, not off a panel beside the arena
 * or, in the headset, a plaque behind it. Every piece of every ring is a thin instance of one
 * quad, unlit: one draw call, whatever the light.
 */
export class ReadyRings {
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private matrices = new Float32Array(0);
  private colors = new Float32Array(0);
  private readonly flashes = new Map<string, number>();
  private readonly wasReady = new Map<string, boolean>();
  private readonly scale = new Vector3();
  private readonly turn = new Quaternion();
  private readonly at = new Vector3();
  private readonly matrix = new Matrix();

  constructor(scene: Scene, parent: TransformNode) {
    // A metre-square quad on the floor, stretched into each piece of arc.
    this.mesh = MeshBuilder.CreateGround('readyRings', { width: 1, height: 1 }, scene);
    this.mesh.parent = parent;
    this.mesh.isPickable = false;
    // Its pieces move with the fighters; the quad's own box at the origin says nothing of them.
    this.mesh.alwaysSelectAsActiveMesh = true;
    this.material = new StandardMaterial('readyRings', scene);
    this.material.disableLighting = true;
    this.material.emissiveColor = WHITE;
    this.material.diffuseColor = Color3.Black();
    this.material.specularColor = Color3.Black();
    // Drawn over the ground it lies a few centimetres above, at any distance.
    this.material.zOffset = -2;
    this.mesh.material = this.material;
    this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    this.mesh.thinInstanceSetBuffer('color', this.colors, 4, false);
    this.mesh.isVisible = false;
  }

  /** Call once per rendered frame with every fighter of the duel. */
  update(fighters: readonly RingFighter[], dt: number): void {
    const count = fighters.length * RING_SEGMENTS;
    if (this.matrices.length !== count * 16) {
      this.matrices = new Float32Array(count * 16);
      this.colors = new Float32Array(count * 4);
      this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
      this.mesh.thinInstanceSetBuffer('color', this.colors, 4, false);
    }
    let shown = false;
    fighters.forEach(({ fighter, pos, yaw, color }, f) => {
      const fill = readiness(fighter);
      const ready = fill >= 1;
      if (ready && this.wasReady.get(fighter.id) === false)
        this.flashes.set(fighter.id, FLASH_SECONDS);
      this.wasReady.set(fighter.id, ready);
      const flash = Math.max(0, (this.flashes.get(fighter.id) ?? 0) - dt);
      this.flashes.set(fighter.id, flash);
      const radius = ringRadius(fighter);
      const arc = ((2 * Math.PI * radius) / RING_SEGMENTS) * (1 - GAP);
      const warning = warningGlow(fighter);
      shown ||= fighter.alive;
      for (let i = 0; i < RING_SEGMENTS; i++) {
        const k = f * RING_SEGMENTS + i;
        // A fallen fighter's ring goes with it.
        if (!fighter.alive) {
          this.matrices.fill(0, k * 16, k * 16 + 16);
          continue;
        }
        // Behind the fighter is its yaw turned half round; each piece stands across the radius.
        const angle = yaw + Math.PI + ((i + 0.5) / RING_SEGMENTS) * 2 * Math.PI;
        this.at.set(
          pos.x + Math.sin(angle) * radius,
          pos.y + LIFT,
          pos.z + Math.cos(angle) * radius,
        );
        this.scale.set(arc, 1, BAND);
        Quaternion.RotationYawPitchRollToRef(angle, 0, 0, this.turn);
        Matrix.ComposeToRef(this.scale, this.turn, this.at, this.matrix);
        this.matrix.copyToArray(this.matrices, k * 16);
        if (warning !== null) {
          this.colors.set(
            [WARNING.r * warning, WARNING.g * warning, WARNING.b * warning, 1],
            k * 4,
          );
          continue;
        }
        const lit = segmentLit(i, fill);
        const c = flash > 0 ? Color3.Lerp(color, WHITE, flash / FLASH_SECONDS) : color;
        // Unlit pieces keep a dark trace of the side's colour: the part still to come.
        const dim = lit ? (ready ? 1 : 0.8) : 0.18;
        this.colors.set([c.r * dim, c.g * dim, c.b * dim, 1], k * 4);
      }
    });
    this.mesh.thinInstanceBufferUpdated('matrix');
    this.mesh.thinInstanceBufferUpdated('color');
    this.mesh.isVisible = shown;
  }

  /** Between duels: nothing shown, no flash kept for the next one. */
  clear(): void {
    this.mesh.isVisible = false;
    this.flashes.clear();
    this.wasReady.clear();
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
  }
}
